import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import { AppLayout } from '@/components/AppLayout';
import { TenantBadge } from '@/components/TenantBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { DataTableSkeleton } from '@/components/skeletons';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { useTenant } from '@/hooks/useTenant';
import { buildTenantDisplayMap, getTenantDisplayById } from '@/lib/tenant-display';
import { LEAD_SOURCE, type LeadStatus, type LeadSource } from '@muraka/shared';
import { Plus, Search, Loader2, Edit, UserCheck, Trash2 } from 'lucide-react';

const statusConfig: Record<LeadStatus, { label: string; class: string }> = {
  new: { label: 'New', class: 'status-new' },
  contacted: { label: 'Contacted', class: 'status-contacted' },
  qualified: { label: 'Qualified', class: 'status-qualified' },
  unqualified: { label: 'Unqualified', class: 'status-unqualified' },
  converted: { label: 'Converted', class: 'status-converted' },
};

const sourceLabels: Record<LeadSource, string> = {
  website: 'Website',
  referral: 'Referral',
  cold_call: 'Cold Call',
  trade_show: 'Trade Show',
  partner: 'Partner',
  other: 'Other',
};

const avatarColors = [
  'bg-blue-100 text-blue-700',
  'bg-green-100 text-green-700',
  'bg-amber-100 text-amber-700',
  'bg-purple-100 text-purple-700',
  'bg-red-100 text-red-700',
  'bg-pink-100 text-pink-700',
];

function getInitials(firstName: string, lastName: string): string {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U';
}

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

function formatDate(date: string): string {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function LeadsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchParams = useSearch({ from: '/leads' });
  const { isTenantContextReady, activeTenantId, mode, tenantList } = useTenant();

  // State
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('');
  const [sourceFilter, setSourceFilter] = useState<LeadSource | ''>('');
  const [createModalOpen, setCreateModalOpen] = useState(() => searchParams.create === 'true');
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [convertingLead, setConvertingLead] = useState<{
    id: string;
    firstName: string;
    lastName: string;
    company: string | null;
  } | null>(null);

  // Clear search param from URL after modal opens
  useEffect(() => {
    if (searchParams.create === 'true') {
      navigate({ to: '/leads', search: {}, replace: true });
    }
  }, [searchParams.create, navigate]);

  // Form state for new lead
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    source: LEAD_SOURCE.WEBSITE as LeadSource,
    notes: '',
  });

  // Convert form state
  const [convertForm, setConvertForm] = useState({
    createOpportunity: false,
    opportunityName: '',
    opportunityAmount: 0,
    opportunityCloseDate: '',
    accountName: '',
  });

  // Query leads
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['leads', mode, activeTenantId, page, search, statusFilter, sourceFilter],
    queryFn: () =>
      api.getLeads({
        page,
        pageSize: 20,
        search: search || undefined,
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
      }),
    enabled: isTenantContextReady,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: api.createLead.bind(api),
    onSuccess: () => {
      toast.success('Lead created successfully');
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setCreateModalOpen(false);
      resetForm();
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to create lead');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: api.deleteLead.bind(api),
    onSuccess: () => {
      toast.success('Lead deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete lead');
    },
  });

  // Convert mutation
  const convertMutation = useMutation({
    mutationFn: (leadId: string) =>
      api.convertLead(leadId, {
        createNewAccount: true,
        accountName: convertForm.accountName || convertingLead?.company || 'New Account',
        createOpportunity: convertForm.createOpportunity,
        opportunityName: convertForm.createOpportunity ? convertForm.opportunityName : undefined,
        opportunityAmount: convertForm.createOpportunity
          ? convertForm.opportunityAmount
          : undefined,
        opportunityCloseDate: convertForm.createOpportunity
          ? new Date(convertForm.opportunityCloseDate).toISOString()
          : undefined,
      }),
    onSuccess: result => {
      toast.success('Lead converted successfully');
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setConvertModalOpen(false);
      navigate({ to: '/accounts/$id', params: { id: result.accountId } });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to convert lead');
    },
  });

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      title: '',
      source: LEAD_SOURCE.WEBSITE,
      notes: '',
    });
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    resetForm();
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email || null,
      phone: formData.phone || null,
      company: formData.company || null,
      title: formData.title || null,
      source: formData.source,
      notes: formData.notes || null,
    });
  };

  const handleDelete = (lead: { id: string; firstName: string; lastName: string }) => {
    if (confirm(`Are you sure you want to delete ${lead.firstName} ${lead.lastName}?`)) {
      deleteMutation.mutate(lead.id);
    }
  };

  const openConvertModal = (lead: {
    id: string;
    firstName: string;
    lastName: string;
    company: string | null;
    status: string;
  }) => {
    if (lead.status === 'converted' || lead.status === 'unqualified') return;
    setConvertingLead(lead);
    const closeDate = new Date();
    closeDate.setMonth(closeDate.getMonth() + 1);
    setConvertForm({
      createOpportunity: false,
      opportunityName: `${lead.company || 'New'} - Opportunity`,
      opportunityAmount: 0,
      opportunityCloseDate: closeDate.toISOString().split('T')[0],
      accountName: lead.company || '',
    });
    setConvertModalOpen(true);
  };

  const handleConvert = (e: React.FormEvent) => {
    e.preventDefault();
    if (convertingLead) {
      convertMutation.mutate(convertingLead.id);
    }
  };

  const leads = data?.data || [];
  const pagination = data?.pagination;
  const showTenantColumn = mode === 'all';
  const tenantDisplayMap = useMemo(() => buildTenantDisplayMap(tenantList), [tenantList]);

  // Calculate status counts
  const getStatusCount = (status: LeadStatus) => {
    return leads.filter(l => l.status === status).length;
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Leads</h1>
            <p className="text-muted-foreground mt-1">Manage your sales leads and prospects</p>
          </div>
          <Button onClick={() => setCreateModalOpen(true)} data-testid="new-lead-button">
            <Plus className="h-4 w-4" />
            New Lead
          </Button>
        </div>

        {/* Filters & Search Bar */}
        <div className="bg-card rounded-xl border border-border">
          <div className="p-4 flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search leads..."
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <Select
                value={statusFilter}
                onChange={e => {
                  setStatusFilter(e.target.value as LeadStatus | '');
                  setPage(1);
                }}
                className="w-auto"
              >
                <option value="">All Statuses</option>
                {Object.entries(statusConfig).map(([value, config]) => (
                  <option key={value} value={value}>
                    {config.label}
                  </option>
                ))}
              </Select>

              <Select
                value={sourceFilter}
                onChange={e => {
                  setSourceFilter(e.target.value as LeadSource | '');
                  setPage(1);
                }}
                className="hidden sm:block w-auto"
              >
                <option value="">All Sources</option>
                {Object.entries(sourceLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Status Pills (Quick Filters) */}
          <div className="px-4 pb-4 flex flex-wrap gap-2">
            <button
              onClick={() => {
                setStatusFilter('');
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === ''
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              All ({leads.length})
            </button>
            {Object.entries(statusConfig).map(([value, config]) => (
              <button
                key={value}
                onClick={() => {
                  setStatusFilter(value as LeadStatus);
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  statusFilter === value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {config.label} ({getStatusCount(value as LeadStatus)})
              </button>
            ))}
          </div>
        </div>

        {/* Leads Table (Desktop) */}
        <div className="hidden lg:block bg-card rounded-xl border border-border overflow-hidden">
          {isLoading ? (
            <div className="p-4">
              <DataTableSkeleton columnCount={showTenantColumn ? 7 : 6} rowCount={8} />
            </div>
          ) : error ? (
            <ErrorState retryAction={() => refetch()} />
          ) : leads.length === 0 ? (
            search || statusFilter || sourceFilter ? (
              <EmptyState
                title="No leads found"
                description="Try adjusting your search or filters to find what you're looking for"
                actionLabel="Clear Query"
                onAction={() => {
                  setSearch('');
                  setStatusFilter('');
                  setSourceFilter('');
                  setPage(1);
                }}
                icon={Search}
              />
            ) : (
              <EmptyState
                title="No leads yet"
                description="Get started by creating your first lead to track potential customers."
                actionLabel="Create Lead"
                onAction={() => setCreateModalOpen(true)}
                icon={UserCheck}
              />
            )
          ) : (
            <table className="w-full" data-testid="leads-table">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    Name
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    Company
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    Status
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    Source
                  </th>
                  {showTenantColumn && (
                    <th className="w-[180px] text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                      Tenant
                    </th>
                  )}
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                    Created
                  </th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border" data-testid="leads-table-body">
                {leads.map(lead => {
                  const fullName = `${lead.firstName} ${lead.lastName}`;
                  const status = statusConfig[lead.status as LeadStatus];
                  const isConverted = lead.status === 'converted';
                  const isUnqualified = lead.status === 'unqualified';
                  const tenantDisplay = getTenantDisplayById(lead.tenantId, tenantDisplayMap);

                  return (
                    <tr
                      key={lead.id}
                      data-testid={`lead-row-${lead.id}`}
                      className={`hover:bg-muted/50 transition-colors cursor-pointer focus-visible:outline-none focus-visible:bg-muted/50 ${
                        isConverted ? 'bg-green-50/50' : ''
                      }`}
                      onClick={() => navigate({ to: '/leads/$id', params: { id: lead.id } })}
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate({ to: '/leads/$id', params: { id: lead.id } });
                        }
                      }}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-9 h-9 rounded-full flex items-center justify-center ${getAvatarColor(fullName)}`}
                          >
                            <span className="text-xs font-medium">
                              {getInitials(lead.firstName, lead.lastName)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{fullName}</p>
                            <p className="text-sm text-muted-foreground">{lead.email || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm text-foreground">{lead.company || '-'}</p>
                        <p className="text-xs text-muted-foreground">{lead.title || ''}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${status.class}`}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-muted-foreground">
                        {sourceLabels[lead.source as LeadSource]}
                      </td>
                      {showTenantColumn && (
                        <td className="px-5 py-4">
                          <TenantBadge
                            tenantName={tenantDisplay.name}
                            brandColor={tenantDisplay.brandColor}
                            density="compact"
                          />
                        </td>
                      )}
                      <td className="px-5 py-4 text-sm text-muted-foreground">
                        {formatDate(lead.createdAt)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => navigate({ to: '/leads/$id', params: { id: lead.id } })}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
                            title="View"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openConvertModal(lead)}
                            disabled={isConverted || isUnqualified}
                            className={`p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted ${
                              isConverted || isUnqualified ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title="Convert"
                            data-testid={`convert-lead-button-${lead.id}`}
                          >
                            <UserCheck className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(lead)}
                            className="p-1.5 rounded-lg text-destructive hover:bg-destructive/10"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {pagination && (
            <div className="px-5 py-4 border-t border-border flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing{' '}
                <span className="font-medium text-foreground">
                  {Math.min((page - 1) * pagination.pageSize + 1, pagination.total)}
                </span>{' '}
                to{' '}
                <span className="font-medium text-foreground">
                  {Math.min(page * pagination.pageSize, pagination.total)}
                </span>{' '}
                of <span className="font-medium text-foreground">{pagination.total}</span> leads
              </p>
              {pagination.totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page === pagination.totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Leads Cards (Mobile/Tablet) */}
        <div className="lg:hidden space-y-3">
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton
                  key={i}
                  className="h-[140px] w-full rounded-xl bg-card border border-border"
                />
              ))}
            </div>
          ) : error ? (
            <ErrorState
              retryAction={() => refetch()}
              className="bg-card rounded-xl border border-border"
            />
          ) : leads.length === 0 ? (
            <EmptyState
              title="No leads found"
              description="Try adjusting your search or filters"
              icon={Plus}
              className="bg-card rounded-xl border border-border"
            />
          ) : (
            leads.map(lead => {
              const fullName = `${lead.firstName} ${lead.lastName}`;
              const status = statusConfig[lead.status as LeadStatus];
              const isConverted = lead.status === 'converted';
              const isUnqualified = lead.status === 'unqualified';
              const tenantDisplay = getTenantDisplayById(lead.tenantId, tenantDisplayMap);

              return (
                <div
                  key={lead.id}
                  className={`bg-card rounded-xl border border-border p-4 hover:border-primary/30 transition-colors ${
                    isConverted ? 'bg-green-50/30' : ''
                  }`}
                >
                  <div
                    className="flex items-start gap-3"
                    onClick={() => navigate({ to: '/leads/$id', params: { id: lead.id } })}
                  >
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${getAvatarColor(fullName)}`}
                    >
                      <span className="text-sm font-medium">
                        {getInitials(lead.firstName, lead.lastName)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-foreground">{fullName}</p>
                          <p className="text-sm text-muted-foreground">{lead.company || '-'}</p>
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0 ${status.class}`}
                        >
                          {status.label}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                        {showTenantColumn && (
                          <TenantBadge
                            tenantName={tenantDisplay.name}
                            brandColor={tenantDisplay.brandColor}
                            className="max-w-[12rem]"
                          />
                        )}
                        {lead.email && (
                          <span className="flex items-center gap-1">
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              />
                            </svg>
                            <span>{lead.email}</span>
                          </span>
                        )}
                        <span>{sourceLabels[lead.source as LeadSource]}</span>
                        <span>{formatDate(lead.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  {/* Mobile Action Buttons */}
                  <div className="mt-3 pt-3 border-t border-border flex items-center justify-end gap-2">
                    <button
                      onClick={() => navigate({ to: '/leads/$id', params: { id: lead.id } })}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                    >
                      <Edit className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    <button
                      onClick={() => openConvertModal(lead)}
                      disabled={isConverted || isUnqualified}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors ${
                        isConverted || isUnqualified ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <UserCheck className="w-3.5 h-3.5" />
                      Convert
                    </button>
                    <button
                      onClick={() => handleDelete(lead)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-destructive bg-destructive/10 rounded-lg hover:bg-destructive/20 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Create Lead Modal */}
      <Dialog open={createModalOpen} onClose={handleCloseCreateModal}>
        <DialogContent onClose={handleCloseCreateModal} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Lead</DialogTitle>
            <DialogDescription>Add a new lead to your pipeline</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-6 p-6 pt-0 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Contact Information */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Contact Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="First Name"
                    required
                    value={formData.firstName}
                    onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="John"
                    data-testid="lead-firstname-input"
                  />
                  <Input
                    label="Last Name"
                    required
                    value={formData.lastName}
                    onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Smith"
                    data-testid="lead-lastname-input"
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@company.com"
                    data-testid="lead-email-input"
                  />
                  <Input
                    label="Phone"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                    data-testid="lead-phone-input"
                  />
                </div>
              </div>

              {/* Company Information */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  Company Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="Company"
                    value={formData.company}
                    onChange={e => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Acme Inc"
                    data-testid="lead-company-input"
                  />
                  <Input
                    label="Job Title"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    placeholder="CEO, CTO, etc."
                    data-testid="lead-title-input"
                  />
                </div>
              </div>

              {/* Lead Details */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  Lead Details
                </h3>
                <Select
                  label="Lead Source"
                  required
                  value={formData.source}
                  onChange={e => setFormData({ ...formData, source: e.target.value as LeadSource })}
                  data-testid="lead-source-select"
                >
                  {Object.entries(sourceLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Notes */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Notes
                </h3>
                <Textarea
                  rows={3}
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes about this lead..."
                  data-testid="lead-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseCreateModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
                data-testid="create-lead-submit"
              >
                {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Lead
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Convert Lead Modal */}
      <Dialog open={convertModalOpen} onClose={() => setConvertModalOpen(false)}>
        <DialogContent onClose={() => setConvertModalOpen(false)} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Convert Lead</DialogTitle>
            <DialogDescription>
              Converting this lead will create an Account and Contact
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConvert}>
            <div className="p-6 pt-0">
              {/* Lead Summary */}
              {convertingLead && (
                <div className="p-4 rounded-lg bg-muted/50 mb-6">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${getAvatarColor(
                        `${convertingLead.firstName} ${convertingLead.lastName}`
                      )}`}
                    >
                      <span>{getInitials(convertingLead.firstName, convertingLead.lastName)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">
                        {convertingLead.firstName} {convertingLead.lastName}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {convertingLead.company || 'No company'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* What will be created */}
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 rounded-lg border border-border">
                  <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center shrink-0">
                    <svg
                      className="w-4 h-4 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm text-foreground">Account will be created</p>
                    <Input
                      className="mt-2"
                      value={convertForm.accountName}
                      onChange={e =>
                        setConvertForm({ ...convertForm, accountName: e.target.value })
                      }
                      placeholder="Account name"
                      data-testid="convert-account-name-input"
                    />
                  </div>
                  <svg
                    className="w-5 h-5 text-green-500 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-lg border border-border">
                  <div className="w-8 h-8 rounded bg-purple-100 flex items-center justify-center shrink-0">
                    <svg
                      className="w-4 h-4 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-foreground">Contact will be created</p>
                    <p className="text-xs text-muted-foreground">
                      {convertingLead?.firstName} {convertingLead?.lastName}
                    </p>
                  </div>
                  <svg
                    className="w-5 h-5 text-green-500 ml-auto shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>

                {/* Opportunity Option */}
                <div className="p-3 rounded-lg border border-border">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={convertForm.createOpportunity}
                      onChange={e =>
                        setConvertForm({ ...convertForm, createOpportunity: e.target.checked })
                      }
                      className="w-4 h-4 rounded border-input text-primary focus:ring-primary"
                      data-testid="convert-create-opportunity-checkbox"
                    />
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-8 h-8 rounded bg-orange-100 flex items-center justify-center shrink-0">
                        <svg
                          className="w-4 h-4 text-orange-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-sm text-foreground">Create an Opportunity</p>
                        <p className="text-xs text-muted-foreground">Start tracking a deal</p>
                      </div>
                    </div>
                  </label>

                  {/* Opportunity Fields */}
                  {convertForm.createOpportunity && (
                    <div className="mt-4 pt-4 border-t border-border space-y-4">
                      <Input
                        label="Opportunity Name"
                        value={convertForm.opportunityName}
                        onChange={e =>
                          setConvertForm({ ...convertForm, opportunityName: e.target.value })
                        }
                        placeholder={`${convertingLead?.company || 'New'} - Opportunity`}
                        data-testid="convert-opportunity-name-input"
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          label="Amount"
                          type="number"
                          value={convertForm.opportunityAmount}
                          onChange={e =>
                            setConvertForm({
                              ...convertForm,
                              opportunityAmount: Number(e.target.value),
                            })
                          }
                          placeholder="0"
                          data-testid="convert-opportunity-amount-input"
                        />
                        <Input
                          label="Close Date"
                          type="date"
                          value={convertForm.opportunityCloseDate}
                          onChange={e =>
                            setConvertForm({ ...convertForm, opportunityCloseDate: e.target.value })
                          }
                          data-testid="convert-opportunity-close-date-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setConvertModalOpen(false)}
                data-testid="convert-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={convertMutation.isPending}
                data-testid="convert-submit-button"
              >
                {convertMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Convert Lead
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
